<script setup lang="ts">
import { computed } from 'vue'

import { formatCredits } from '../../service/creditPresentation'
import { useCreditStore } from '../../stores/creditStore'

import { useI18n } from '@/shared/i18n'

const model = defineModel<number | null>({ default: null })
const { t } = useI18n()
const creditStore = useCreditStore()

const inputValue = computed<string | number>({
  get: () => model.value ?? '',
  set: (value) => {
    if (value === '') {
      model.value = null
      return
    }

    const numericValue = Number(value)
    model.value = Number.isFinite(numericValue) ? Math.max(0, Math.trunc(numericValue)) : null
  },
})
const balanceText = computed(() =>
  t('credits.maxCredits.balanceValue', { count: creditStore.remaining }),
)
const exceedsBalance = computed(() => model.value !== null && model.value > creditStore.remaining)
</script>

<template>
  <section
    class="max-credits-field grid gap-sm p-lg rounded-lg bg-[color-mix(in_srgb,_var(--ui-color-info)_8%,_var(--ui-color-surface))]"
    :aria-label="t('credits.maxCredits.title')"
  >
    <div class="max-credits-field__row flex items-start justify-between gap-lg">
      <div class="max-credits-field__heading grid min-w-0 gap-0.5">
        <p class="max-credits-field__title m-0 text-primary text-[0.875rem] font-bold">
          {{ t('credits.maxCredits.title') }}
        </p>
        <p class="max-credits-field__description m-0 text-muted text-xs leading-normal">
          {{ t('credits.maxCredits.description') }}
        </p>
      </div>
      <div class="max-credits-field__input flex shrink-0 items-center gap-2">
        <input
          v-model.number="inputValue"
          type="number"
          min="0"
          placeholder="—"
          :aria-label="t('credits.maxCredits.label')"
        />
        <span>{{ t('credits.maxCredits.creditsSuffix') }}</span>
      </div>
    </div>

    <hr
      class="max-credits-field__divider m-0 border-0 border-t border-t-[color-mix(in_srgb,_var(--ui-color-info)_25%,_var(--ui-color-border))]"
    />

    <div
      class="max-credits-field__row max-credits-field__row--balance flex items-start justify-between gap-lg items-center text-[0.75rem]"
    >
      <span class="max-credits-field__balance text-muted">
        {{ t('credits.maxCredits.balance') }}
        <strong>{{ balanceText }}</strong>
      </span>
      <span v-if="exceedsBalance" class="max-credits-field__warning text-warning font-semibold">
        {{
          t('credits.maxCredits.exceedsBalance', { balance: formatCredits(creditStore.remaining) })
        }}
      </span>
    </div>
  </section>
</template>

<style scoped>
.max-credits-field .max-credits-field__input input {
  width: 6rem;
  padding-block: 0.5rem;
  padding-inline: 0.75rem;
  border: 1px solid var(--ui-color-border);
  border-radius: var(--ui-radius-md);
  background-color: var(--ui-color-surface);
  color: var(--ui-color-primary);
  font-family: var(--ui-font-sans);
  font-size: 0.875rem;
  font-weight: 600;
  text-align: center;
}
.max-credits-field .max-credits-field__input input:focus-visible {
  border-color: var(--ui-color-primary);
  outline-style: none;
}
.max-credits-field .max-credits-field__input span {
  color: var(--ui-color-muted);
  font-size: 0.875rem;
  white-space: nowrap;
}
.max-credits-field .max-credits-field__balance strong {
  color: var(--ui-color-primary);
  font-weight: 700;
}
</style>
